import { Track } from "@/src/lib/tracking";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import { AnimatePresence } from "motion/react";
import { cn, dayjs } from "utils";
import { useSnapshot } from "valtio";
import isEqual from "react-fast-compare";
import { LottieLoading } from "@ospweb/sdk-ui";
import CheckBox from "../CheckBox";
import { SPIN_STATUS } from "../Spin";
import { TradeCardInfo } from "../TradeCardInfo";
import { RealTimeResult } from "../RealTimeResult";
import { ChatTabs } from "../ChatModal/ChatTabs";
import { type SourceType } from "./Source";
import RelatedAccounts, { type User } from "./RelatedAccounts";
import Related from "./Related/RelatedList";
import Action from "./Action";
import Answer from "./Answer";
import FileTagAttachmentList from "./FileTagAttachmentList";
import SpinMessage from "./SpinMessage";
import { DeepSearch } from "./DeepSearch/DeepSearch";
import CardContainer from "./Cards/CardContainer";
import { ChatCategoryCardType } from "./Cards/type";
import XPayReportCard from "./Cards/XPayReportCard";
import Thinking from "./Thinking";
import { transformCardsToXPayData } from "./Cards/utils/xPayDataTransform";
import { ContentNavigationMenu } from "./ContentNavigationMenu";
import { OthersReportPanelWithData } from "./OthersReportPanel";
import { searchModeMap } from "./const";
import { XPayReportData } from "./Cards/XPayReportTypes";
import type { MessageAttachment } from "./FileTagAttachmentList";
import MemberStatusCard from "./MemberStatusCard";
import { parseDynamicUiBlocks } from "./jsonRender/parseDynamicUiBlocks";
import { type ChatJsonRenderContextValue } from "./jsonRender/ChatJsonRenderContext";
import { usePathname } from "@hooks/usePathname";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Chat/Message";
import { useIsPc } from "@hooks/useIsPc";
import { deepSearchStatus, type useChats } from "@/src/hooks/useChats";
import { ChatStore } from "@store/modules/chat";
import { rootStore } from "@store/rootStore";
import { useDeepCompareMemo } from "@/src/hooks/useDeepCompareMemo";
import { Cards } from "@features/api/ChatApi";
import { useChatMemberStatusCard } from "@hooks/useChatMemberStatusCard";
import { useMarkdownNavigation } from "@hooks/useMarkdownNavigation";
import { useMemberUser } from "@hooks/useMemberUser";
import { useRequestPortfolioActions } from "@hooks/useRequestPortfolioActions";
import { usePresentRiskAndGoalsFlow } from "@hooks/onboarding/usePresentRiskAndGoalsFlow";
import { useAgentMessage } from "@/src/hooks/useAgentMessage";
import {
  processDeepsearchAgentChunks,
  processNonReportAgentChunks,
} from "@utils/groupByAgent";
import { resolveUserMessagePresentation } from "@utils/chatUtils/parseChatMessage";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import {
  SaveGoalCard,
  hasSaveGoalResult,
  TrackerChatCard,
  parseStartTrackingArgs,
} from "./InteractiveTool";
import type {
  SaveGoalArgs,
  SaveGoalCardSubmitPayload,
} from "@components/Chat/InteractiveTool/types";
import {
  buildStartTrackingResult,
  hasStartTrackingResult,
} from "./InteractiveTool/TrackerChatCard/parseStartTrackingResult";
import { parseConfirmSharpenArgs } from "../fitness/sharpen/parseConfirmSharpenArgs";
import { SharpenConfirmCard } from "../fitness/sharpen/chat-cards/SharpenConfirmCard";
import { useSharpenConfirmSubmit } from "../fitness/sharpen/hooks/useSharpenConfirmSubmit";
import { useTrackerActivatePaywallGate } from "@/src/hooks/tracker/useTrackerActivatePaywallGate";
import { AskUserQuestionCard } from "../ChatOnboarding/AskUserQuestionCard";
import { AskUserQuestionResultCards } from "../ChatOnboarding/AskUserQuestionResultCard";
import { InteractiveToolResultCard } from "../ChatOnboarding/InteractiveToolResultCard";
import {
  hasAskUserQuestionResult,
  parseAskUserQuestionResult,
} from "../ChatOnboarding/parseAskUserQuestionResult";
import {
  hasRequestPortfolioResult,
  parseRequestPortfolioResult,
} from "../ChatOnboarding/parseRequestPortfolioResult";
import {
  isRequestPortfolioArgs,
  resolveRequestPortfolioLabels,
} from "../ChatOnboarding/resolveRequestPortfolioLabels";
import { RequestPortfolioCard } from "../ChatOnboarding/RequestPortfolioCard";
import { resolvePresentRiskAndGoalsPresentation } from "../ChatOnboarding/parsePresentRiskAndGoalsArgs";
import { resolveInteractiveToolOnly } from "@utils/chatUtils/resolveInteractiveToolOnly";
import type {
  AskUserQuestionAnswer,
  AskUserQuestionArgsInput,
  AskUserQuestionToolInfo,
} from "../ChatOnboarding/types";
import { mapPayloadToDiagnosisView } from "@components/ChatOnboarding/Steps/DiagnosisUnlockFlow/mapPresentRiskToDiagnosisUnlockView";
import { CheckupFindingsChatCard } from "./InteractiveTool/CheckupFindingsChatCard";
import { UserMessageQuestionContent } from "./UserMessageQuestionContent";
import type { SenderAttachment } from "../Sender/type";
const I18n = I18nUtil.createScoped(Translations);

// 只代表点击按钮进来的，prompt触发的360为chat，不展示360 report card
const is360Report = "360";

type MessagePropsType = {
  showSelect?: boolean;
  selected?: string[];
  onSelect?: (selected: string[]) => void;
  data: ReturnType<typeof useChats>["messages"][0] & {
    rating?: string;
    sources?: unknown[];
    users?: unknown[];
    cards?: ChatCategoryCardType[];
    attachments?: unknown[];
    metadata?: unknown;
    interactiveToolCalls?: unknown;
    /** role: tool 时由 appendToolMessage 写入，与 interactiveToolCalls.result 语义一致 */
    toolName?: string;
    toolCallId?: string;
  };
  minHeight?: number;
  streamLoading?: boolean;
  entering?: boolean;
  isLast?: boolean;
  messageId?: string;
  chatId?: string;
  reload?: () => void;
  onShare?: (selected: string[]) => void;
  onSendRelatedValue?: (inputString: string) => void;
  error?: Error;
  isAction?: boolean;
  isStartAnswer?: boolean;
  detailId?: string;
  nextIsAssistant?: boolean;
  prevIsUser?: boolean;
  messageLength?: number;
  prevMessageId?: string | string[];
  role?: string;
  isDeepSearchAgent?: boolean;
  onGetRealTimeResult?: () => void;
  currentIndex: number; // 添加currentIndex属性用于区分唯一性
  isTab?: boolean;
  isOnlyChat?: boolean;
  shareId?: string;
  onSubmitAskUserQuestion?: (params: {
    toolCallId: string;
    toolName: string;
    chatId: string;
    content: string;
    attachments?: SenderAttachment[];
    interactiveToolOnly?: boolean;
  }) => void;
  /** save_goal 确认：单条 tool 消息，interactiveToolOnly=true */
  onSubmitSaveGoalConfirmed?: (params: {
    toolCallId: string;
    toolName: string;
    chatId: string;
    payload?: Record<string, unknown>;
  }) => void | Promise<void>;
  /**
   * 是否展示底部输入框（Sender）。
   * 有输入框 → interactiveToolOnly=false；纯卡片问答 → true。
   */
  showChatInput?: boolean;
  trackingScene?: string;
  isOnborading?: boolean;
};

/** chatsDetail.interactive_tool_calls 单项的宽松类型（Message 内再做窄化校验） */
type InteractiveToolCallLike = {
  toolCallId?: string;
  toolName?: string;
  /** 交互态：题目与选项（ask_user_question 的 questions 等） */
  args?: unknown;
  /**
   * 查看态：用户已提交答案。
   * ask_user_question 下多为 JSON.stringify(AskUserQuestionAnswer[])；
   * 由 parseAskUserQuestionResult 解析，见 ask-user-question-component-dev.md §3.3
   */
  result?: unknown;
};

const isMessageAttachment = (value: any): value is MessageAttachment => {
  return (
    value?.type === "file" &&
    typeof value?.filename === "string" &&
    typeof value?.url === "string"
  );
};

const parseMessageAttachments = (value: unknown): MessageAttachment[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isMessageAttachment);
};

const extractMessageAttachments = (message: MessagePropsType["data"]) => {
  const directAttachments = parseMessageAttachments(message?.attachments);
  if (directAttachments.length > 0) {
    return directAttachments;
  }

  const metadataRaw = message?.metadata;
  if (!metadataRaw) {
    return [];
  }

  let metadata: any = metadataRaw;
  if (typeof metadataRaw === "string") {
    try {
      metadata = JSON.parse(metadataRaw);
    } catch {
      return [];
    }
  }

  return parseMessageAttachments(metadata?.messageParams?.attachments);
};

const MNT_FILE_TAG_REGEX = /<mnt_file>sandbox:\/mnt\/data\/(.+?)<\/mnt_file>/g;

const extractInlineFileAttachments = (content: string): MessageAttachment[] => {
  const attachments: MessageAttachment[] = [];
  let match: RegExpExecArray | null;
  while ((match = MNT_FILE_TAG_REGEX.exec(content)) !== null) {
    const fileName = match[1];
    attachments.push({
      type: "file",
      filename: fileName,
      url: `sandbox:/mnt/data/${fileName}`,
    });
  }
  MNT_FILE_TAG_REGEX.lastIndex = 0;
  return attachments;
};

const stripInlineFileTags = (content: string): string => {
  return content.replace(MNT_FILE_TAG_REGEX, "").trim();
};

const replaceInlineFileTagsWithElements = (
  content: string,
  userId?: string,
  chatId?: string,
  shareId?: string,
): string => {
  return content.replace(MNT_FILE_TAG_REGEX, (_, fileName) => {
    const attrs = [
      `data-filename="${fileName}"`,
      `data-url="sandbox:/mnt/data/${fileName}"`,
      userId ? `data-userid="${userId}"` : "",
      chatId ? `data-chatid="${chatId}"` : "",
      shareId ? `data-shareid="${shareId}"` : "",
    ]
      .filter(Boolean)
      .join(" ");
    return `<edgenattachment ${attrs}></edgenattachment>`;
  });
};

const Message: FC<MessagePropsType> = ({
  data,
  showSelect,
  selected = [],
  onSelect,
  minHeight,
  streamLoading,
  messageId = "",
  chatId = "",
  isLast,
  reload = () => {},
  onShare = () => {},
  isAction = true,
  error,
  isStartAnswer,
  onSendRelatedValue,
  messageLength,
  prevMessageId,
  isDeepSearchAgent,
  currentIndex = 0, // 添加currentIndex参数，默认值为0
  isTab,
  isOnlyChat,
  shareId,
  onSubmitAskUserQuestion,
  onSubmitSaveGoalConfirmed,
  showChatInput,
  trackingScene,
  isOnborading,
}) => {
  const pathName = usePathname();
  const { data: memberInfo } = useMemberUser();

  const { memberStatus, cardProps, isAppPayingRenewLoading } =
    useChatMemberStatusCard();
  const isSharePage = pathName.indexOf("share") > -1;
  const isUserMessage = data?.role === "user";
  /** 用户提交交互式工具答案后产生的 tool message；content 等价于 interactiveToolCalls.result */
  const isToolMessage = data?.role === "tool";
  const isApiDeepsearch =
    !!data.deepsearch || data?.searchMode === searchModeMap.deepsearch;
  const enabled = (isDeepSearchAgent || isApiDeepsearch) && !isUserMessage;
  const { isLoggedIn } = useSnapshot(rootStore.login);

  // deepsearch history agent message(reasoning/header/summary/toolcall)
  const { data: agentMessage, isLoading } = useAgentMessage({
    chatId,
    messageId,
    enabled: enabled && typeof data?.searchMode !== "undefined",
  });
  const fetchLoading = streamLoading || isLoading;
  const newDeepSearchData =
    processDeepsearchAgentChunks(agentMessage || [], {
      allowedToolName: "TavilySearchResults",
    }) || {};
  const newDeepSearchStepList =
    newDeepSearchData?.deepsearch
      ?.filter((item) => item.type === "summary")
      ?.map((item) => item.data) || [];

  const stepList = newDeepSearchStepList?.map((item) => {
    return {
      ...item,
      status: deepSearchStatus.FINISH,
      id: `deep-search-${item?.id}`,
    };
  });

  // 当是最后一条消息且用户没有积分时，展示会员状态卡片（资格条件）
  const memberStatusEligible =
    localStorage.getItem(LOCAL_STORAGE_KEYS.HIDDEN_CHAT_ID) === chatId &&
    isLast &&
    !isUserMessage &&
    isLoggedIn;

  // 仅在内容高度 >= 200px 时才展示卡片，防止内容未加载时出现大量空白
  const answerRef = useRef<HTMLDivElement>(null);
  const [answerTallEnough, setAnswerTallEnough] = useState(false);
  useEffect(() => {
    if (!memberStatusEligible) {
      setAnswerTallEnough(false);
      return;
    }
    const el = answerRef.current;
    if (!el) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setAnswerTallEnough(entry.contentRect.height >= 200);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [memberStatusEligible]);
  const isShowMemberStatusCard = memberStatusEligible && answerTallEnough;

  const reasoningData =
    agentMessage?.filter((item: any) => item.agent === "reasoning") || [];
  const toolCallList = newDeepSearchData?.deepsearch?.map((item) => item.data);
  const reasoning =
    processNonReportAgentChunks(reasoningData)?.reasoning?.[0]?.data?.text;

  const isShowDeepSearch =
    isApiDeepsearch &&
    (data?.toolCallList?.length > 0 ||
      data.reasoning ||
      enabled ||
      reasoning ||
      toolCallList?.length > 0);

  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [isPC] = useIsPc();
  const { isDeepsearch } = useSnapshot(ChatStore);
  const { ownerInfo, fingerprint } = useSnapshot(rootStore.user);
  const [activeKey, setActiveKey] = useState<string>("result");

  const rawContent = data?.content || "";
  const metadataAttachments = useMemo(
    () => extractMessageAttachments(data),
    [data],
  );
  const attachmentUserId = ownerInfo?.id || fingerprint || undefined;
  const attachmentChatId = chatId || undefined;
  const hasInlineFileTags = useMemo(() => {
    MNT_FILE_TAG_REGEX.lastIndex = 0;
    return MNT_FILE_TAG_REGEX.test(rawContent);
  }, [rawContent]);
  // When inline file tags exist, replace them with <edgenattachment> elements for inline rendering;
  // otherwise strip them (no-op) and use the raw content as-is.
  const messageContent = useMemo(() => {
    MNT_FILE_TAG_REGEX.lastIndex = 0;
    if (hasInlineFileTags) {
      return replaceInlineFileTagsWithElements(
        rawContent,
        attachmentUserId,
        attachmentChatId,
        shareId,
      );
    }
    return stripInlineFileTags(rawContent);
  }, [
    rawContent,
    hasInlineFileTags,
    attachmentUserId,
    attachmentChatId,
    shareId,
  ]);
  const inlineFileAttachments = useMemo(
    () => extractInlineFileAttachments(rawContent),
    [rawContent],
  );
  // For user messages, show both metadata + inline attachments at bottom;
  // for assistant messages, inline attachments are rendered within the markdown.
  const messageAttachments = useMemo(
    () =>
      data?.role === "user"
        ? [...metadataAttachments, ...inlineFileAttachments]
        : metadataAttachments,
    [metadataAttachments, inlineFileAttachments, data?.role],
  );
  const dynamicUiResultRaw = useMemo(
    () =>
      isUserMessage || isToolMessage
        ? {
            markdown: isToolMessage ? "" : messageContent,
            items: [],
            hasPendingDynamicUi: false,
          }
        : parseDynamicUiBlocks(messageContent),
    [isUserMessage, isToolMessage, messageContent],
  );
  const stableItemsRef = useRef<{
    key: string;
    items: typeof dynamicUiResultRaw.items;
  }>({ key: "", items: [] });

  const dynamicUiResult = useMemo(() => {
    const raw = dynamicUiResultRaw;
    const newKey = JSON.stringify(raw.items.map((i) => i.spec));
    if (newKey === stableItemsRef.current.key) {
      return { ...raw, items: stableItemsRef.current.items };
    }
    stableItemsRef.current = { key: newKey, items: raw.items };
    return raw;
  }, [dynamicUiResultRaw]);
  const hasDynamicUiItems = dynamicUiResult.items.length > 0;

  // --- Interactive tool（onboarding）---
  // 文档：features/20260529-onboarding-design/frontend-flow.md
  // assistant：interactiveToolCalls[0] — 有 result → 查看态；无 result → 交互态。
  // role:tool：content 等价于 interactiveToolCalls[0].result（展示前已由 mergeAndFilter 合并进 assistant）。
  // request_portfolio：Connect → handleImportClick；Other ways → Snap a photo / Upload a statement / Maybe later。

  /** assistant 取 interactiveToolCalls[0]；孤立 tool message 用 content 作为 result */
  const interactiveToolCall = useMemo(():
    InteractiveToolCallLike | undefined => {
    if (isToolMessage) {
      if (!data?.toolName || !data?.toolCallId) {
        return undefined;
      }

      return {
        toolCallId: data.toolCallId,
        toolName: data.toolName,
        result: data.content,
      };
    }

    const interactiveToolCalls = Array.isArray(data?.interactiveToolCalls)
      ? (data.interactiveToolCalls as InteractiveToolCallLike[])
      : [];
    return interactiveToolCalls[0];
  }, [
    data?.content,
    data?.interactiveToolCalls,
    data?.toolCallId,
    data?.toolName,
    isToolMessage,
  ]);

  /** 当前 tool 的 toolCallId / toolName，提交 tool message 时原样带回 */
  const interactiveToolInfo = useMemo<
    AskUserQuestionToolInfo | undefined
  >(() => {
    if (!interactiveToolCall?.toolCallId || !interactiveToolCall?.toolName) {
      return undefined;
    }

    return {
      toolCallId: interactiveToolCall.toolCallId,
      toolName: interactiveToolCall.toolName,
    };
  }, [interactiveToolCall]);

  /**
   * 向 Chat 上层提交 role:tool 消息（appendToolMessage）。
   * content 因工具而异；toolCallId / toolName / chatId 始终来自当前 interactiveToolCall。
   */
  const submitInteractiveToolMessage = useCallback(
    (
      content: string,
      options?: {
        interactiveToolOnly?: boolean;
        attachments?: SenderAttachment[];
      },
    ) => {
      if (!interactiveToolInfo || !onSubmitAskUserQuestion || !chatId) {
        return;
      }

      onSubmitAskUserQuestion({
        toolCallId: interactiveToolInfo.toolCallId,
        toolName: interactiveToolInfo.toolName,
        chatId,
        content,
        attachments: options?.attachments,
        interactiveToolOnly:
          options?.interactiveToolOnly ??
          resolveInteractiveToolOnly(showChatInput),
      });
    },
    [interactiveToolInfo, onSubmitAskUserQuestion, chatId, showChatInput],
  );

  const requestPortfolioArgs = useMemo(() => {
    if (interactiveToolCall?.toolName !== "request_portfolio") {
      return undefined;
    }

    const args = interactiveToolCall.args;
    return isRequestPortfolioArgs(args) ? args : undefined;
  }, [interactiveToolCall]);

  const requestPortfolioLabels = useMemo(
    () => resolveRequestPortfolioLabels(requestPortfolioArgs),
    [requestPortfolioArgs],
  );

  const { handleAction: handleRequestPortfolioAction } =
    useRequestPortfolioActions({
      onSubmit: submitInteractiveToolMessage,
    });

  const askUserQuestionToolCall =
    interactiveToolCall?.toolName === "ask_user_question"
      ? interactiveToolCall
      : undefined;

  /** 查看态数据源：result 解析后的答案列表，多题则多项 */
  const askUserQuestionResults = useMemo(
    () => parseAskUserQuestionResult(askUserQuestionToolCall?.result),
    [askUserQuestionToolCall?.result],
  );

  /** 为 true 时不渲染 AskUserQuestionCard，避免已答题消息再次出现选项 UI */
  const hasAskUserQuestionAnswered = useMemo(
    () => hasAskUserQuestionResult(askUserQuestionToolCall?.result),
    [askUserQuestionToolCall?.result],
  );

  /** 交互态：仅在没有有效 result 时使用 args.questions */
  const askUserQuestionArgs = useMemo<
    AskUserQuestionArgsInput | undefined
  >(() => {
    if (
      hasAskUserQuestionAnswered ||
      !(askUserQuestionToolCall?.args as any)?.questions?.length
    ) {
      return undefined;
    }

    return askUserQuestionToolCall?.args as
      AskUserQuestionArgsInput | undefined;
  }, [askUserQuestionToolCall?.args, hasAskUserQuestionAnswered]);

  /**
   * ask_user_question 交互态提交：content 为 AskUserQuestionAnswer[] JSON。
   */
  const handleSubmitAskUserQuestion = useMemo(() => {
    if (
      !interactiveToolInfo ||
      interactiveToolInfo.toolName !== "ask_user_question" ||
      !onSubmitAskUserQuestion
    ) {
      return undefined;
    }

    return (answers: AskUserQuestionAnswer[]) => {
      submitInteractiveToolMessage(JSON.stringify(answers));
    };
  }, [
    interactiveToolInfo,
    onSubmitAskUserQuestion,
    submitInteractiveToolMessage,
  ]);

  /** request_portfolio 查看态：result / role:tool content → 标题 + 操作文案 */
  const requestPortfolioResultDisplay = useMemo(
    () =>
      parseRequestPortfolioResult(
        interactiveToolCall?.toolName === "request_portfolio"
          ? interactiveToolCall.result
          : undefined,
        requestPortfolioArgs,
      ),
    [interactiveToolCall, requestPortfolioArgs],
  );

  const saveGoalArgs = useMemo<SaveGoalArgs | undefined>(() => {
    if (interactiveToolCall?.toolName !== "save_goal") {
      return undefined;
    }

    return interactiveToolCall.args as SaveGoalArgs;
  }, [interactiveToolCall]);

  const { runActivate: runTrackerActivate, isActivating: isTrackerActivating } =
    useTrackerActivatePaywallGate();

  const [startTrackingResultOverride, setStartTrackingResultOverride] =
    useState<string | null>(null);

  const startTrackingArgs = useMemo(() => {
    if (interactiveToolCall?.toolName !== "start_tracking") {
      return undefined;
    }
    return parseStartTrackingArgs(interactiveToolCall.args);
  }, [interactiveToolCall]);

  const startTrackingEffectiveResult = useMemo(() => {
    if (startTrackingResultOverride != null) {
      return startTrackingResultOverride;
    }
    return interactiveToolCall?.toolName === "start_tracking"
      ? interactiveToolCall.result
      : undefined;
  }, [interactiveToolCall, startTrackingResultOverride]);

  const isStartTrackingTracked = useMemo(
    () => hasStartTrackingResult(startTrackingEffectiveResult),
    [startTrackingEffectiveResult],
  );

  const confirmSharpenArgs = useMemo(() => {
    if (interactiveToolCall?.toolName !== "confirm_sharpen_score") {
      return undefined;
    }
    return parseConfirmSharpenArgs(interactiveToolCall.args);
  }, [interactiveToolCall]);

  const { confirm: confirmSharpenScore, isSubmitting: isSharpenConfirming } =
    useSharpenConfirmSubmit({
      toolInfo: interactiveToolInfo,
      chatId,
      submitInteractiveToolMessage,
    });

  const handleStartTracking = useCallback(async () => {
    if (!startTrackingArgs || isStartTrackingTracked) {
      return;
    }

    const outcome = await runTrackerActivate({
      trackerId: startTrackingArgs.tracker_id,
      entry: "chat_start_tracking",
    });

    if (outcome.outcome === "activated") {
      setStartTrackingResultOverride(
        buildStartTrackingResult(outcome.trackerId),
      );
    }
  }, [isStartTrackingTracked, runTrackerActivate, startTrackingArgs]);

  const hasInteractiveToolResult = useMemo(() => {
    if (!interactiveToolCall?.toolName) {
      return false;
    }

    const { toolName, result } = interactiveToolCall;
    switch (toolName) {
      case "ask_user_question":
        return hasAskUserQuestionResult(result);
      case "request_portfolio":
        return hasRequestPortfolioResult(result);
      case "save_goal":
        return hasSaveGoalResult(result);
      case "start_tracking":
        return hasStartTrackingResult(result) || isStartTrackingTracked;
      case "confirm_sharpen_score":
        return result != null && result !== "";
      default:
        return result != null && result !== "";
    }
  }, [interactiveToolCall, isStartTrackingTracked]);

  /** 非最后一条消息且尚无 result 时，禁用交互卡片内操作 */
  const isInteractiveToolActionDisabled = !isLast && !hasInteractiveToolResult;

  const presentRiskAndGoalsPresentation = useMemo(() => {
    return resolvePresentRiskAndGoalsPresentation(interactiveToolCall?.args);
  }, [interactiveToolCall]);

  const { handleBuildPlanClick, isCompletingOnboarding } =
    usePresentRiskAndGoalsFlow({
      presentation: presentRiskAndGoalsPresentation,
      isLast,
      enabled: interactiveToolCall?.toolName === "present_risk_and_goals",
      hasResult: !!interactiveToolCall?.result,
      isActionDisabled: isInteractiveToolActionDisabled,
      submitInteractiveToolMessage,
    });

  const handleSaveGoalSubmit = useCallback(
    async (payload: SaveGoalCardSubmitPayload) => {
      if (
        payload.type === "confirmed" &&
        interactiveToolInfo &&
        onSubmitSaveGoalConfirmed &&
        chatId
      ) {
        await onSubmitSaveGoalConfirmed({
          toolCallId: interactiveToolInfo.toolCallId,
          toolName: interactiveToolInfo.toolName,
          chatId,
          payload,
        });
        return;
      }

      submitInteractiveToolMessage(JSON.stringify(payload));
    },
    [
      chatId,
      interactiveToolInfo,
      onSubmitSaveGoalConfirmed,
      submitInteractiveToolMessage,
    ],
  );

  /** 按 toolName 渲染对应卡片；插入在消息正文区域（见 JSX 内 interactiveToolCard） */
  const interactiveToolCard = useMemo(() => {
    switch (interactiveToolCall?.toolName) {
      case "ask_user_question":
        // 查看态：assistant.result 或 role:tool 的 content（后端亦写入 result）
        if (askUserQuestionResults.length > 0) {
          return (
            <AskUserQuestionResultCards
              answers={askUserQuestionResults}
              toolInfo={interactiveToolInfo}
            />
          );
        }

        // tool message 仅展示查看态，不回落到交互态
        if (isToolMessage) {
          return null;
        }

        // 交互态（无 result，展示选项 / 翻题 / Send）
        return askUserQuestionArgs ? (
          <AskUserQuestionCard
            args={askUserQuestionArgs}
            toolInfo={interactiveToolInfo}
            className="mb-3"
            disabled={isInteractiveToolActionDisabled}
            onSubmit={handleSubmitAskUserQuestion}
            trackingScene={trackingScene}
          />
        ) : null;
      case "request_portfolio":
        if (requestPortfolioResultDisplay) {
          return (
            <InteractiveToolResultCard
              title={requestPortfolioResultDisplay.title}
              body={requestPortfolioResultDisplay.body}
              toolInfo={interactiveToolInfo}
            />
          );
        }

        if (isToolMessage) {
          return null;
        }

        return (
          <RequestPortfolioCard
            labels={requestPortfolioLabels}
            toolInfo={interactiveToolInfo}
            disabled={isInteractiveToolActionDisabled}
            onAction={handleRequestPortfolioAction}
            onFeedback={submitInteractiveToolMessage}
            trackingScene={trackingScene}
          />
        );
      case "save_goal":
        if (isToolMessage) {
          return null;
        }

        return saveGoalArgs ? (
          <SaveGoalCard
            args={saveGoalArgs}
            result={interactiveToolCall.result}
            toolInfo={interactiveToolInfo}
            chatId={chatId}
            className="mb-3"
            disabled={isInteractiveToolActionDisabled}
            onSubmit={handleSaveGoalSubmit}
          />
        ) : null;
      case "start_tracking":
        if (isToolMessage) {
          return null;
        }

        return startTrackingArgs ? (
          <TrackerChatCard
            args={startTrackingArgs}
            isTracked={isStartTrackingTracked}
            disabled={isInteractiveToolActionDisabled}
            isActivating={isTrackerActivating}
            onStartTracking={handleStartTracking}
            className="mb-3"
          />
        ) : null;
      case "confirm_sharpen_score":
        if (isToolMessage) {
          return null;
        }

        return confirmSharpenArgs ? (
          <SharpenConfirmCard
            summary={confirmSharpenArgs.summary}
            facts={confirmSharpenArgs.facts}
            disabled={isInteractiveToolActionDisabled}
            isSubmitting={isSharpenConfirming}
            onConfirm={() => {
              void confirmSharpenScore();
            }}
            className="mb-3"
          />
        ) : null;
      case "present_risk_and_goals":
        if (isToolMessage) {
          return null;
        }

        if (presentRiskAndGoalsPresentation) {
          const diagnosisView = mapPayloadToDiagnosisView(
            presentRiskAndGoalsPresentation.payload,
          );

          return (
            <CheckupFindingsChatCard
              className="mb-3"
              riskCount={diagnosisView.findings.length}
              findings={diagnosisView.findings}
              result={interactiveToolCall?.result}
              disabled={isInteractiveToolActionDisabled}
              isSubmitting={isCompletingOnboarding}
              toolInfo={interactiveToolInfo}
              onBuildPlanClick={() => {
                void handleBuildPlanClick();
              }}
            />
          );
        }

        if (!interactiveToolCall?.result) {
          return (
            <div>
              {I18n.t(
                "Got it — I've got a good read on you. Let me look a little closer",
              )}
            </div>
          );
        }

        return null;
      default:
        return null;
    }
  }, [
    interactiveToolCall,
    askUserQuestionResults,
    askUserQuestionArgs,
    interactiveToolInfo,
    handleSubmitAskUserQuestion,
    requestPortfolioLabels,
    requestPortfolioResultDisplay,
    handleRequestPortfolioAction,
    submitInteractiveToolMessage,
    trackingScene,
    saveGoalArgs,
    handleSaveGoalSubmit,
    startTrackingArgs,
    isStartTrackingTracked,
    isTrackerActivating,
    handleStartTracking,
    confirmSharpenArgs,
    isSharpenConfirming,
    confirmSharpenScore,
    isToolMessage,
    isInteractiveToolActionDisabled,
    presentRiskAndGoalsPresentation,
    handleBuildPlanClick,
    isCompletingOnboarding,
  ]);
  const chatJsonRenderContext = useMemo<ChatJsonRenderContextValue>(
    () => ({
      chatId: attachmentChatId,
      streamLoading,
    }),
    [attachmentChatId, streamLoading],
  );

  const userMessagePresentation =
    resolveUserMessagePresentation(messageContent);
  const referenceContent =
    userMessagePresentation.kind === "user-input"
      ? userMessagePresentation.referenceContent
      : undefined;
  const questionContent = userMessagePresentation.text;

  const { navigationItems, activeItemId, handleItemClick } =
    useMarkdownNavigation({
      content:
        messageContent && !isUserMessage && !isToolMessage
          ? messageContent
          : undefined,
      currentIndex, // 将currentIndex传递给useMarkdownNavigation hook
    });
  const status = useDeepCompareMemo(() => {
    // 1 理解问题 2 检索问题 3 总结问题
    if ((isDeepsearch && !error && data.reasoning) || !isLast) {
      return {
        type: SPIN_STATUS.SUCCESS,
      };
    }
    // item 为user 时候展示的loading
    if (!isStartAnswer && isUserMessage && streamLoading) {
      return {
        type: SPIN_STATUS.LOADING,
        text: "Hold tight, cooking some Alpha for you...",
      };
    }
    if (streamLoading && !messageContent) {
      // const len = flow.length - 1;
      return {
        type: SPIN_STATUS.THINKING,
        // text: flow[len]?.message || "Hold tight, cooking some Alpha for you...",
      };
    }
    return {
      type: SPIN_STATUS.SUCCESS,
    };
  }, [
    isDeepsearch,
    error,
    data,
    isLast,
    isStartAnswer,
    isUserMessage,
    streamLoading,
    messageContent,
  ]);

  const showContent = useMemo(() => {
    if (
      !isStartAnswer ||
      (isStartAnswer && !isUserMessage) ||
      (!isUserMessage && !streamLoading) ||
      error
    ) {
      return true;
    }
    return false;
  }, [error, isStartAnswer, isUserMessage, streamLoading]);

  const showContentResolved = useMemo(() => {
    if (isToolMessage && interactiveToolCard) {
      return true;
    }
    return showContent;
  }, [isToolMessage, interactiveToolCard, showContent]);

  const showAction = useDeepCompareMemo(() => {
    if (isToolMessage) {
      return false;
    }
    if (
      !data?.users?.length &&
      !data?.users?.length &&
      !data?.sources?.length &&
      !data?.sources?.length &&
      !data?.content
    ) {
      return false;
    }
    if (!isAction || isShowMemberStatusCard) {
      return false;
    }
    if (streamLoading) {
      return !isLast;
    }
    if (showSelect) {
      return false;
    }
    return true;
  }, [
    isToolMessage,
    isAction,
    streamLoading,
    data,
    showSelect,
    isShowMemberStatusCard,
    isLast,
  ]);

  const shouldRenderAssistantMarkdown =
    !isUserMessage && !isToolMessage && !!messageContent;
  const shouldRenderAnswerBlock =
    !isUserMessage &&
    (shouldRenderAssistantMarkdown ||
      hasDynamicUiItems ||
      !!interactiveToolCard);

  const isApiAgent = data?.searchMode === searchModeMap.agent;

  const isShowAnswer =
    (shouldRenderAssistantMarkdown ||
      hasDynamicUiItems ||
      !!interactiveToolCard) &&
    !isUserMessage;

  const isShowSource = data?.sources?.length > 0;
  const cardData = useMemo(
    () => (data?.cards as ChatCategoryCardType[]) || [],
    [data?.cards],
  );

  const isShowCard = cardData.length > 0;
  const isShowXPayReportCard = useMemo(() => {
    if (!isShowCard) {
      return false;
    }
    return cardData.find((item: Cards) => item?.answer_type === is360Report);
  }, [cardData, isShowCard]);

  // report 水印
  const containerStyle = isShowXPayReportCard
    ? {
        backgroundImage: 'url("/assets/images/edgen-report.svg")',
        backgroundRepeat: "repeat",
        backgroundSize: "166px 58px",
        backgroundPosition: "center",
      }
    : {};
  const sourceData = (data?.sources as SourceType[]) || [];

  // XPayReportCard数据转换
  const xPayData = useMemo(() => {
    if (!isShowXPayReportCard) {
      return undefined;
    }

    if (!cardData[0]) {
      return undefined;
    }

    return transformCardsToXPayData(cardData[0]);
  }, [isShowXPayReportCard, cardData]);

  const handleShare = () => {
    if (data?.role === "assistant") {
      const prevMessageIds = Array.isArray(prevMessageId)
        ? prevMessageId
        : [prevMessageId || ""];
      onShare(selected.concat([...prevMessageIds, messageId || ""]));
      return;
    }
    onShare(selected.concat(messageId || ""));
  };

  useEffect(() => {
    const handleIframeMessage = (event: MessageEvent) => {
      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        if (data.messageType === "pageLoaded" && data.action === "post") {
          setIsIframeLoaded(true);
        }
      } catch (error) {
        Track.log("error", "Message", "Error handling iframe message:", {
          cause: String(error),
        });
      }
    };

    window.addEventListener("message", handleIframeMessage);

    return () => {
      window.removeEventListener("message", handleIframeMessage);
    };
  }, []);

  const handleTabClick = (key: string) => {
    setActiveKey(key);
  };

  const isShowTab = false;
  const isTabLoading = false;

  const timeStorageKey = useMemo(() => {
    return `thinking_time_${chatId}`;
  }, [chatId]);

  if (
    isUserMessage &&
    userMessagePresentation.kind === "silent-entry" &&
    messageAttachments.length === 0
  ) {
    return null;
  }

  return (
    <AnimatePresence key={data?.id}>
      <div
        className={cn("flex w-full mx-auto mb-4", {
          "hover:bg-background_white_overlay5/5 hover:rounded-2xl md:pr-4 flex-row group/tableStyle":
            showSelect,
          ["max-w-[900px]"]: isShowXPayReportCard,
        })}
        style={{
          minHeight: minHeight || "auto",
        }}
        data-tracking-scope="chat-message"
      >
        <div id="message-checkbox" className={cn("flex items-center")}>
          <CheckBox
            data-openreplay-label="chatbox_message_checkbox"
            checked={!!selected?.includes(messageId || "")}
            classNames={cn(
              "flex-shrink-0 mx-4",
              showSelect ? "flex" : "hidden",
            )}
            onClick={() => {
              if (selected?.includes(messageId || "")) {
                onSelect?.(selected?.filter((id) => id !== messageId));
              } else {
                onSelect?.(selected.concat(messageId || ""));
              }
            }}
          />
        </div>
        <div
          className={cn(
            "duration-300 flex-col flex-1 text-content_secondary w-full",
          )}
        >
          {isUserMessage && (
            <div className="flex flex-col w-full py-4 gap-3">
              {referenceContent && (
                <div className="flex flex-row gap-2 items-center justify-end">
                  <div className="bg-background_white_overlay20 w-0.5 h-[19px]"></div>
                  <div className="body_m text-content_tertiary line-clamp-1">
                    {referenceContent}
                  </div>
                </div>
              )}
              {!!messageAttachments.length && (
                <FileTagAttachmentList
                  attachments={messageAttachments}
                  userId={attachmentUserId}
                  chatId={attachmentChatId}
                  shareId={shareId}
                  align="right"
                />
              )}
              {userMessagePresentation.kind === "action-echo" && (
                <div className="flex justify-end">
                  <div className="body_m max-w-full break-all whitespace-pre-wrap rounded-3 border border-border_brand_transparent20 bg-background_brand_overlay5 px-3 py-1.5 text-content_brand_secondary">
                    <UserMessageQuestionContent content={questionContent} />
                  </div>
                </div>
              )}
              {userMessagePresentation.kind === "user-input" &&
                !!questionContent?.trim() && (
                  <div className="flex justify-end">
                    <div className="body_l break-all text-content_primary bg-background_white_overlay5 py-2 px-3 rounded-3 max-w-full whitespace-pre-wrap">
                      <UserMessageQuestionContent content={questionContent} />
                    </div>
                  </div>
                )}
            </div>
          )}
          {showContentResolved && (
            <SpinMessage reload={reload} status={status}>
              {/* 弹窗里面的时候，需要展示成tab的交互形式 */}
              <ChatTabs
                data-openreplay-label="chatbox_message_chattabs"
                onClick={handleTabClick}
                className={isTabLoading || !isShowTab ? "hidden" : ""}
                activeKey={activeKey}
                time={isLast ? localStorage.getItem(timeStorageKey) || "" : ""}
              />
              <div className={cn("w-full")}>
                {!isToolMessage &&
                  (!isTab ||
                    isTabLoading ||
                    !isShowTab ||
                    (isTab && activeKey === "thinking")) &&
                  (isShowDeepSearch ? (
                    <DeepSearch
                      data={{
                        toolCallList: toolCallList || data.toolCallList,
                        stepList:
                          data?.stepList?.length > 0
                            ? data?.stepList
                            : stepList,
                        count: sourceData?.length,
                        reasoning: data.reasoning || reasoning,
                      }}
                      isLast={isLast}
                      loading={streamLoading}
                      content={!!messageContent}
                    />
                  ) : (
                    (isApiAgent ||
                      (streamLoading &&
                        isLast &&
                        typeof data?.searchMode === "undefined")) &&
                    !isUserMessage && (
                      <Thinking
                        agents={data?.agents}
                        chatId={chatId}
                        messageId={messageId || ""}
                        isShowAnswer={isShowAnswer}
                        isLast={isLast}
                        loading={streamLoading}
                        createdAt={data?.createdAt}
                        agentTypes={data?.agentTypes || []}
                        isTab={isTab}
                        noAnimate={isTab && activeKey === "thinking"}
                        // 管理tab为thinking时的接口是否调用
                        enabled={
                          isTab &&
                          activeKey === "thinking" &&
                          (!isLast || (isLast && !streamLoading))
                        }
                      />
                    )
                  ))}
                {(!isTab ||
                  isTabLoading ||
                  !isShowTab ||
                  (isTab && activeKey === "result")) && (
                  <>
                    {streamLoading &&
                      !messageContent &&
                      !isUserMessage &&
                      isDeepSearchAgent && (
                        <div className="flex items-center body_m text-content_secondary justify-start rounded-xl pr-3 py-2 gap-2 flex-grow-0 w-fit">
                          <LottieLoading style={{ width: 40, height: 40 }} />
                          {I18n.t(
                            "Brewing your report ☕  This may take a few minutes - Page departure would abort mission",
                          )}
                        </div>
                      )}
                    {/* deepSearch的时候source在toolcall中，不展示source弹窗入口, 普通search需要展示在普通思考流之后 */}
                    {/* 添加agent 思考流的时候，去除正文source展示 —— 1.25迭代 */}
                    {/* {isShowSource && !data.reasoning ? (
                             <BasedSource data={sourceData} />
                           ) : null} */}
                    {/* {data?.createdAt && (
                             <div className="body_s text-content_tertiary">
                               {data?.createdAt}
                             </div>
                           )} */}
                    {messageContent && shouldRenderAssistantMarkdown && (
                      <div
                        className={cn(
                          "gap-1 items-center body_s text-content_quaternary pt-1",
                        )}
                      >
                        <span>{I18n.t("Data Acquired on:")}</span>
                        <span>
                          {dayjs(data?.generateAt || data?.createdAt).format(
                            "MMM DD YYYY HH:mm:ss",
                          )}
                        </span>
                      </div>
                    )}

                    {Number(data?.users?.length) > 0 && (
                      <RelatedAccounts data={(data?.users as User[]) || []} />
                    )}
                    {/* 显示卡片容器内容 */}
                    {isShowCard &&
                      status.type === SPIN_STATUS.SUCCESS &&
                      (isShowXPayReportCard ? (
                        <XPayReportCard
                          data={
                            {
                              ...xPayData,
                              forecastUrl: isOnlyChat
                                ? ""
                                : xPayData?.forecastUrl,
                              price: isOnlyChat
                                ? {
                                    ...xPayData?.price,
                                    label: "",
                                  }
                                : xPayData?.price,
                            } as XPayReportData
                          }
                        />
                      ) : (
                        <CardContainer
                          cards={cardData}
                          createdAt={data?.createdAt}
                          messageId={messageId}
                          chatId={chatId}
                          enabled={!fetchLoading}
                        />
                      ))}
                    <div className="flex gap-8">
                      {isShowXPayReportCard && isPC && (
                        <div className="w-[248px] flex-shrink-0 sticky top-[66px] h-fit mt-6">
                          {messageContent && (
                            <ContentNavigationMenu
                              menuItems={navigationItems}
                              activeItemId={activeItemId}
                              onItemClick={handleItemClick}
                              isLoading={
                                navigationItems.length < 1 && fetchLoading
                              }
                            />
                          )}
                          {messageContent && !isSharePage && (
                            <OthersReportPanelWithData
                              className="mt-8"
                              cardType={cardData?.[0]?.type || ""}
                              limit={5}
                              themes={cardData?.[0]?.properties?.themes}
                              enabled={!fetchLoading}
                              id={cardData?.[0]?.properties?.id}
                              market={cardData?.[0]?.properties?.market}
                            />
                          )}
                        </div>
                      )}
                      <div className="min-w-0 w-full">
                        {shouldRenderAnswerBlock && (
                          <div className={cn("w-full")}>
                            <div className="relative" ref={answerRef}>
                              {(shouldRenderAssistantMarkdown ||
                                hasDynamicUiItems) && (
                                <Answer
                                  markdown={dynamicUiResult.markdown}
                                  dynamicUiItems={dynamicUiResult.items}
                                  dynamicUiRenderContext={chatJsonRenderContext}
                                  source={
                                    isShowSource && !streamLoading
                                      ? sourceData
                                      : []
                                  }
                                  className={cn({
                                    "h-[200px] overflow-hidden":
                                      isShowMemberStatusCard,
                                  })}
                                  entering={streamLoading && isLast}
                                  showSelect={showSelect}
                                  containerStyle={containerStyle}
                                  enableNavigation={navigationItems.length > 0}
                                  currentIndex={currentIndex} // 将currentIndex传递给Answer组件
                                />
                              )}
                              {interactiveToolCard}
                              {isShowMemberStatusCard && (
                                <div
                                  className="pointer-events-none absolute bottom-0 left-1/2 h-[118px] w-full -translate-x-1/2 z-[2]"
                                  style={{
                                    background:
                                      "linear-gradient(180deg, rgba(248, 246, 242, 0.00) 0%, #F8F6F2 100%)",
                                  }}
                                />
                              )}
                            </div>
                            {isShowMemberStatusCard && (
                              <MemberStatusCard
                                memberStatus={memberStatus}
                                {...cardProps}
                                isAppPayingRenewLoading={
                                  isAppPayingRenewLoading
                                }
                                className="mt-2"
                              />
                            )}
                          </div>
                        )}
                        {!messageContent &&
                          !isOnborading &&
                          status.type === SPIN_STATUS.SUCCESS &&
                          !isShowDeepSearch &&
                          !isUserMessage &&
                          !isToolMessage &&
                          !interactiveToolCard &&
                          !messageAttachments.length && (
                            <div className="w-full">
                              {I18n.t(
                                "Alpha canceled due to impatience. Found a spicier question?",
                              )}
                            </div>
                          )}
                        {!isUserMessage && !!messageAttachments.length && (
                          <FileTagAttachmentList
                            attachments={messageAttachments}
                            userId={attachmentUserId}
                            chatId={attachmentChatId}
                            shareId={shareId}
                            className={cn(messageContent ? "mt-3" : "")}
                          />
                        )}

                        {isDeepSearchAgent &&
                          isIframeLoaded &&
                          !isUserMessage &&
                          memberInfo?.isExpert && (
                            <RealTimeResult
                              data-openreplay-label="chatbox_message_realtimeresult"
                              className="my-3"
                              text={dayjs(
                                data?.generateAt || data?.createdAt,
                              ).format("MMM DD YYYY")}
                              onClick={() => {
                                reload?.();
                              }}
                            />
                          )}

                        {isIframeLoaded && !isUserMessage && <TradeCardInfo />}

                        {showAction && (
                          <Action
                            rating={data.rating as "thumb_up" | "thumb_down"}
                            reload={reload}
                            onShare={handleShare}
                            markdown={messageContent}
                            messageId={messageId || ""}
                            isTab={isTab}
                            retry={
                              !!isLast &&
                              data.role === "assistant" &&
                              messageLength !== 1
                            }
                            isDeepSearchAgent={isDeepSearchAgent || false}
                          />
                        )}
                        {!isPC &&
                          messageContent &&
                          !isSharePage &&
                          !isShowMemberStatusCard && (
                            <OthersReportPanelWithData
                              className="my-8"
                              cardType={cardData?.[0]?.type || ""}
                              limit={5}
                              themes={cardData?.[0]?.properties?.themes}
                              id={cardData?.[0]?.properties?.id}
                              market={cardData?.[0]?.properties?.market}
                            />
                          )}
                        {!isShowMemberStatusCard && (
                          <Related
                            isLast={isLast}
                            streamLoading={streamLoading}
                            data={(Array.isArray(data?.relatedQuestions)
                              ? data?.relatedQuestions
                              : []
                            ).slice(0, 3)}
                            onSendRelatedValue={onSendRelatedValue}
                            itemClassName={
                              isTab ? "bg-background_white_overlay5" : ""
                            }
                          />
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </SpinMessage>
          )}
        </div>
      </div>
    </AnimatePresence>
  );
};

// Custom equality function for memo
const arePropsEqual = (prevProps: any, nextProps: any) => {
  // Only re-render if these props change
  return isEqual(prevProps, nextProps);
};

export default memo(Message, arePropsEqual);
